import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Factory,
  Zap,
  Droplets,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  Shield,
  Download,
  ArrowRight
} from 'lucide-react';

const CaseStudies = () => {
  const [showDownloadPopup, setShowDownloadPopup] = useState(false);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState('');
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', company: '' });

  const handleDownloadClick = (caseStudyTitle: string) => {
    setSelectedCaseStudy(caseStudyTitle);
    setShowDownloadPopup(true);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email) {
      alert('Please fill in all required fields');
      return;
    }

    const message = `Case Study Download Request:

Name: ${formData.name}
Phone: ${formData.phone}
Email: ${formData.email}
Company: ${formData.company || 'Not specified'}
Case Study: ${selectedCaseStudy}`;
    
    const whatsappUrl = `https://wa.me/919944574442?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    setShowDownloadPopup(false);
    setFormData({ name: '', phone: '', email: '', company: '' });
  };
  const caseStudies = [
    {
      id: 1,
      industry: 'Paper Manufacturing',
      client: 'Paper Manufacturing Plant',
      icon: Factory,
      title: 'Preventing a Major Production Breakdown',
      challenge: 'A critical bearing in a key production roll was showing hidden signs of failure. To the naked eye, the machine looked fine, but vibration levels were dangerously high—putting the entire production line at risk of an unexpected shutdown.',
      solution: 'Used advanced vibration analysis and FFT diagnostics to identify an outer race defect in the bearing. Verified abnormal frequencies and confirmed the damage through inspection. Scheduled the replacement during planned maintenance to avoid unplanned downtime.',
      implementation: [
        'Advanced vibration analysis and FFT diagnostics',
        'Identified outer race defect in bearing',
        'Verified abnormal frequencies through inspection',
        'Scheduled replacement during planned maintenance',
        'Continuous monitoring to prevent future issues'
      ],
      results: [
        { metric: 'Vibration Reduction', value: '85%', icon: TrendingDown, color: 'text-green-600' },
        { metric: 'Vibration Level', value: '1.3 mm/s', icon: TrendingDown, color: 'text-green-600' },
        { metric: 'Fault Frequencies', value: 'Resolved', icon: Shield, color: 'text-green-600' },
        { metric: 'Unplanned Downtime', value: 'Prevented', icon: Shield, color: 'text-green-600' }
      ],
      testimonial: {
        quote: "The advanced diagnostics prevented catastrophic bearing seizure and potential line shutdown, avoiding unplanned emergency repairs and production losses.",
        author: "Production Manager"
      },
      timeline: 'Immediate Action',
      roi: 'Prevented Major Loss',
      image: '/images/industry/paper.jpg',
      businessImpact: [
        'Prevented catastrophic bearing seizure and potential line shutdown',
        'Avoided unplanned emergency repairs and production losses',
        'Improved machine reliability and extended asset lifespan through predictive maintenance'
      ]
    },
    {
      id: 2,
      industry: 'Captive Power Plant (Paper Industry)',
      client: 'Paper Industry Power Plant',
      icon: Zap,
      title: 'Saving a Power Plant Fan from Failure',
      challenge: 'A forced draft fan, critical to plant operations, began showing high vibration levels. Left unchecked, the fault could have led to complete fan failure—causing power outages, production stoppage, and significant financial losses.',
      solution: 'Continuous monitoring detected abnormal vibration patterns and repetitive impacts. Diagnostic analysis revealed coupling damage and misalignment. Recommended precision alignment and bearing clearance checks. Damaged coupling grids were replaced during planned maintenance.',
      implementation: [
        'Continuous monitoring for abnormal vibration patterns',
        'Diagnostic analysis revealed coupling damage and misalignment',
        'Precision alignment and bearing clearance checks',
        'Coupling grid replacement during planned maintenance',
        'Real-time predictive insights implementation'
      ],
      results: [
        { metric: 'Vibration Reduction', value: '23%', icon: TrendingDown, color: 'text-green-600' },
        { metric: 'Emergency Breakdowns', value: '0', icon: Shield, color: 'text-green-600' },
        { metric: 'Plant Schedule', value: 'No Disruption', icon: Shield, color: 'text-green-600' },
        { metric: 'Potential Savings', value: '₹35-40 Lakh', icon: DollarSign, color: 'text-green-600' }
      ],
      testimonial: {
        quote: "Avoided a potential ₹35–40 lakh loss due to unplanned shutdown and prevented 5–6 days of downtime through predictive maintenance.",
        author: "Plant Operations Manager"
      },
      timeline: 'Quick Response',
      roi: '₹35-40 Lakh Saved',
      image: '/images/industry/power.jpg',
      businessImpact: [
        'Avoided a potential ₹35–40 lakh loss due to unplanned shutdown',
        'Prevented 5–6 days of downtime and emergency repair costs',
        'Ensured reliable power supply to the production line',
        'Improved confidence in asset monitoring through real-time predictive insights'
      ]
    },
    {
      id: 3,
      industry: 'Fertilizer & Chemicals',
      client: 'Chemical Manufacturing Plant',
      icon: Droplets,
      title: 'Restoring Stability to a High-Speed Air Blower',
      challenge: 'A critical air blower, recently serviced and restarted, began showing severe vibration levels above 17 mm/s. Within just days, the machine tripped automatically to prevent destruction. At 5000+ RPM, every second of operation was accelerating damage, putting the plant\'s continuous air supply—and production—at serious risk.',
      solution: 'Conducted detailed vibration and phase analysis across bearings and mounting points. Identified dominant 2X frequency components and phase mismatch indicating structural issues. Root cause was traced not to the gearbox itself but to improper foundation mounting. Applied blue matching diagnostics, detected uneven contact and pitting at the base plate. Machined the base plate, corrected the foundation, and restored proper gearbox alignment.',
      implementation: [
        'Detailed vibration and phase analysis across bearings and mounting points',
        'Identified 2X frequency components and phase mismatch',
        'Root cause analysis traced to foundation mounting issues',
        'Blue matching diagnostics for base plate inspection',
        'Foundation correction and gearbox alignment restoration'
      ],
      results: [
        { metric: 'Vibration Reduction', value: '87%', icon: TrendingDown, color: 'text-green-600' },
        { metric: 'Final Vibration', value: '2.2 mm/s', icon: TrendingDown, color: 'text-green-600' },
        { metric: 'Machine Status', value: 'Stabilized', icon: Shield, color: 'text-green-600' },
        { metric: 'Potential Savings', value: '₹50+ Lakh', icon: DollarSign, color: 'text-green-600' }
      ],
      testimonial: {
        quote: "Fixing the root cause instead of chasing symptoms not only saved critical equipment but also ensured uninterrupted operations. The foundation was the silent culprit.",
        author: "Maintenance Engineer"
      },
      timeline: 'Critical Response',
      roi: '₹50+ Lakh Saved',
      image: '/images/industry/fertilizer.jpg',
      businessImpact: [
        'Prevented potential ₹50+ lakh loss from extended shutdown and equipment damage',
        'Avoided weeks of downtime and costly emergency repairs',
        'Strengthened the structural integrity of the machine for long-term reliability',
        'Delivered sustainable improvement by addressing the real root cause—foundation correction'
      ]
    }
  ];

  const overallStats = [
    { label: 'ROI Achievement', value: '20X', icon: Factory },
    { label: 'Machine Availability', value: '99.98%', icon: TrendingUp },
    { label: 'Downtime Reduction', value: '98%', icon: TrendingDown },
    { label: 'Continued Support', value: '24×7', icon: Shield }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F0F4FE] via-white to-[#E2EEFE] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
              Real Results from <span className="text-[#233790]">Real Clients</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover how organizations across industries have transformed their operations 
              through our asset management solutions and achieved measurable results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overall Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {overallStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-[#233790] to-[#3BA6EA] rounded-lg flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-light text-gray-900 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {/* Image */}
                  <div className="relative h-64 lg:h-auto">
                    <img
                      src={study.image}
                      alt={study.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#233790]/20 to-transparent"></div>
                    <div className="absolute top-6 left-6">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-lg">
                          <study.icon className="w-6 h-6 text-[#233790]" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{study.industry}</p>
                          <p className="text-blue-100 text-sm">{study.client}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8 lg:p-12">
                    <h2 className="text-2xl font-light text-gray-900 mb-6">{study.title}</h2>
                    
                    <div className="space-y-6 mb-8">
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Challenge</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{study.challenge}</p>
                      </div>
                      
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Solution</h3>
                        <p className="text-gray-600 text-sm leading-relaxed mb-3">{study.solution}</p>
                        <ul className="space-y-1">
                          {study.implementation.slice(0, 3).map((item, idx) => (
                            <li key={idx} className="text-xs text-gray-500 flex items-start">
                              <div className="w-1 h-1 bg-[#233790] rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Results Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className={`flex items-center justify-center mb-1 ${result.color}`}>
                            <result.icon className="w-4 h-4 mr-1" />
                            <span className="font-semibold">{result.value}</span>
                          </div>
                          <p className="text-xs text-gray-600">{result.metric}</p>
                        </div>
                      ))}
                    </div>

                    {/* Key Metrics */}
                    <div className="flex justify-between items-center mb-6 p-4 bg-blue-50 rounded-lg">
                      <div className="text-center">
                        <p className="text-2xl font-light text-[#233790]">{study.timeline}</p>
                        <p className="text-xs text-gray-600">Implementation</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-light text-[#233790]">{study.roi}</p>
                        <p className="text-xs text-gray-600">ROI</p>
                      </div>
                    </div>

                    {/* Business Impact */}
                    {study.businessImpact && (
                      <div className="mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3">Business Impact</h3>
                        <ul className="space-y-2">
                          {study.businessImpact.map((impact, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-start">
                              <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                              {impact}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Testimonial */}
                    <blockquote className="border-l-4 border-[#233790] pl-4 mb-6">
                      <p className="text-gray-700 italic text-sm leading-relaxed mb-2">
                        "{study.testimonial.quote}"
                      </p>
                      <cite className="text-xs text-gray-500">— {study.testimonial.author}</cite>
                    </blockquote>

                    <button 
                      onClick={() => handleDownloadClick(study.title)}
                      className="w-full sm:w-auto inline-flex items-center px-6 py-2 border border-[#233790] text-[#233790] font-medium rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Full Case Study
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Bigger Picture */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#233790] to-[#3BA6EA] rounded-full mb-6">
              <span className="text-2xl">🌟</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              The Bigger Picture
            </h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Many failures are blamed on bearings, gearboxes, or couplings—but sometimes, the foundation is the silent culprit. 
                In our chemical plant case, fixing the root cause instead of chasing symptoms not only saved critical equipment 
                but also ensured uninterrupted operations.
              </p>
              <p className="text-xl text-[#233790] font-medium">
                With AAMS by Aswartha, we don't just monitor—we uncover the hidden causes of failure and engineer lasting solutions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#F0F4FE] to-[#E2EEFE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
              Ready to Become Our Next Success Story?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join the growing list of organizations achieving exceptional results through 
              intelligent asset management and predictive maintenance strategies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-3 bg-[#233790] text-white font-medium rounded-lg hover:bg-[#1e2d73] transition-colors"
              >
                Start Your Transformation
              </a>
              <a
                href="/services"
                className="inline-flex items-center px-8 py-3 border border-[#233790] text-[#233790] font-medium rounded-lg hover:bg-[#F0F4FE] transition-colors"
              >
                Explore Our Services <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Download Popup */}
      {showDownloadPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowDownloadPopup(false)}>
          <div className="bg-white rounded-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#233790] to-[#3BA6EA] text-white p-4 rounded-t-xl flex items-center justify-between">
              <h2 className="text-lg font-semibold">Download Case Study</h2>
              <button onClick={() => setShowDownloadPopup(false)} className="p-1 hover:bg-white/10 rounded">
                ×
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-gray-600 text-sm mb-4">Fill the form below to download the complete case study</p>
              
              <form onSubmit={handleFormSubmit} className="space-y-3">
                <input
                  type="text"
                  placeholder="Full Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#233790] focus:border-transparent"
                />
                
                <input
                  type="tel"
                  placeholder="Phone Number *"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#233790] focus:border-transparent"
                />
                
                <input
                  type="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#233790] focus:border-transparent"
                />
                
                <input
                  type="text"
                  placeholder="Company Name (Optional)"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#233790] focus:border-transparent"
                />
                
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#233790] to-[#3BA6EA] text-white py-3 rounded-lg font-medium hover:from-[#1e2d73] hover:to-[#2691d9] transition-all"
                >
                  Submit & Download
                </button>
                
                <p className="text-xs text-gray-600 text-center mt-2">
                  You'll be redirected to WhatsApp to complete your request
                </p>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseStudies;